import { useContext, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import {
  useCreateTemplateInstance,
  useDeleteTemplateInstance,
  useListSignatureTemplates,
  useSendEnvelope,
  useSignatureService,
  useSignatureTemplateSearch,
  useUpdateEnvelope,
} from '~/core/shared/api/signature';
import { FormField, Input } from '~/core/shared/components';
import { DateTimeDisplay } from '~/core/shared/components/DateTimeDisplay';
import { ControlledSelect } from '~/core/shared/components/form/ControlledSelect';
import { useRequiredParams } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Envelope, TemplateInstance } from '~/core/shared/types';
import { Includes } from '~/core/shared/types/utility';
import { handleSuccess } from '~/core/shared/utils/admin';
import { ApplicationContext } from '../application/Layout.component';
import { EnvelopeDraftEditor } from './EnvelopeDraftEditor.component';
import { TemplateInstanceForm } from './TemplateInstanceForm.component';

type EnvelopeFormProps = {
  envelope: Envelope;
  envelopesListingPath: string;
};

type EnvelopeFormValues = {
  subject: string;
};

export const EnvelopeForm = ({
  envelope: initialEnvelope,
  envelopesListingPath,
}: EnvelopeFormProps) => {
  const [envelope, setEnvelope] = useState<Includes<Envelope, 'instances'>>({
    instances: [],
    ...initialEnvelope,
  });
  const { templates } = useListSignatureTemplates(
    envelope.id,
    envelope.instances,
  );
  const {
    options: templateOptions,
    isLoading: isTemplatesLoading,
    inputValue: templateInputValue,
    onInputChange: onTemplateInputChange,
  } = useSignatureTemplateSearch(envelope.id);
  const { createTemplateInstance } = useCreateTemplateInstance();
  const { deleteTemplateInstance } = useDeleteTemplateInstance();
  const { updateEnvelope } = useUpdateEnvelope();
  const { sendEnvelope } = useSendEnvelope();
  const { definition } = useContext(ApplicationContext);
  const { uuid } = useRequiredParams(['uuid']);
  const navigate = useNavigate();
  const form = useForm<EnvelopeFormValues>({
    defaultValues: initialEnvelope,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEditor, setShowEditor] = useState<boolean>(false);

  const { service } = useSignatureService(envelope.service_identifier);

  const canAddTemplate = useMemo(
    () =>
      (service && service.supports.multiple_templates) ||
      envelope.instances.length < 1,
    [service, envelope.instances],
  );

  const handleCreateTemplateInstance = async (templateId: string) => {
    const instance = await createTemplateInstance({
      envelope_id: envelope.id,
      template_id: templateId,
    });

    if (instance) {
      setShowEditor(false);
      setEnvelope({
        ...envelope,
        instances: [...envelope.instances, instance],
      });
    }
  };

  const handleTemplateSelect = useEventHandler((templateId: string | null) => {
    if (templateId) {
      handleCreateTemplateInstance(templateId);
    }
  });

  const handleDeleteTemplateInstance = async (instance: TemplateInstance) => {
    try {
      await deleteTemplateInstance(instance.id);
      setShowEditor(false);
      setEnvelope({
        ...envelope,
        instances: envelope.instances.filter(({ id }) => id !== instance.id),
      });
    } catch {
      // error handled in useDeleteTemplateInstance
    }
  };

  const toggleEditor = () => {
    setShowEditor(!showEditor);
  };

  const runProcess = async (process: () => Promise<void>) => {
    setIsProcessing(true);
    await process();
    setIsProcessing(false);
  };

  const saveForm = async (formValues: any): Promise<boolean> => {
    try {
      await updateEnvelope({ envelopeId: envelope.id, data: formValues });
      return true;
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        Object.entries(
          e.response.data.errors as Record<string, string[]>,
        ).forEach(([name, errors]) =>
          form.setError(name as any, { message: errors[0] }),
        );
      }
      // error handled in useUpdateEnvelope
      return false;
    }
  };

  const onSubmit = async (formValues: any): Promise<void> =>
    runProcess(async () => {
      const saved = await saveForm(formValues);

      if (saved) {
        handleSuccess('Envelope saved.');
        navigate(generatePath(envelopesListingPath, { uuid }));
      }
    });

  const handleSendEnvelope = async (formValues: any): Promise<void> =>
    runProcess(async () => {
      const saved = await saveForm(formValues);

      if (saved) {
        try {
          await sendEnvelope({
            envelopeId: envelope.id,
            documentParams: { definition },
            params: { include: 'instances' },
          });
          navigate(generatePath(envelopesListingPath, { uuid }));
        } catch {
          // handled in useSendEnvelope hook
        }
      }
    });

  if (!service) {
    return <Spinner />;
  }

  return (
    <Stack gap={8}>
      <FormProvider {...form}>
        <Stack as="form" gap={8} onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            component={Input}
            label="Name (Visible in Broker Portal)"
            name="name"
            required
          />
          <FormField component={Input} label="Subject" name="subject" />
          <FormField
            component={Input}
            defaultValue={envelope.status || 'draft'}
            hidden
            name="status"
            required
          />
          {/* Hidden submit button to catch browser key events */}
          <Button display="none" type="submit" />
        </Stack>
      </FormProvider>
      {canAddTemplate && (
        <ControlledSelect
          inputValue={templateInputValue}
          isLoading={isTemplatesLoading}
          name="template"
          onChange={handleTemplateSelect}
          onInputChange={onTemplateInputChange}
          options={templateOptions}
          placeholder="Choose or search templates"
          searchable
          value={null}
        />
      )}
      {(envelope?.instances || []).map((instance) => {
        const template = templates?.find((t) => t.id === instance.template_id);
        return (
          <TemplateInstanceForm
            key={instance.id}
            instance={instance}
            onDelete={handleDeleteTemplateInstance}
            template={template}
          />
        );
      })}
      <Flex justifyContent="space-between">
        <HStack gap={6}>
          <ButtonGroup>
            <Button
              colorPalette="primary"
              loading={isProcessing}
              onClick={form.handleSubmit(onSubmit)}
              type="submit"
              variant="solid"
            >
              Save and exit
            </Button>
            {service?.supports.draft_editor && (
              <Button
                colorPalette={showEditor ? 'primary' : 'gray'}
                onClick={toggleEditor}
                variant={showEditor ? 'solid' : 'outline'}
              >
                {showEditor ? 'Close' : 'Open'} Draft Editor
              </Button>
            )}
          </ButtonGroup>
          <HStack color="navy.500" gap={12}>
            <Box>
              <Text as="b">Created At</Text>
              <Text>
                <DateTimeDisplay value={envelope.created_at} />
              </Text>
            </Box>
            <Box>
              <Text as="b">Created By</Text>
              <Text>
                {envelope.created_by ? envelope.created_by.name : 'System'}
              </Text>
            </Box>
            <Box>
              <Text as="b">Status</Text>
              <Text>{envelope.latest_log?.status ?? 'Draft'}</Text>
            </Box>
            {envelope.sent_at && (
              <Box>
                <Text as="b">Sent By</Text>
                <Text>
                  {envelope.sent_by ? envelope.sent_by.name : 'System'}
                </Text>
              </Box>
            )}
            {envelope.sent_at && (
              <Box>
                <Text as="b">Sent At</Text>
                <Text>
                  <DateTimeDisplay value={envelope.sent_at} />
                </Text>
              </Box>
            )}
          </HStack>
        </HStack>
        <Button
          loading={isProcessing}
          onClick={form.handleSubmit(handleSendEnvelope)}
          type="submit"
        >
          Send and exit
        </Button>
      </Flex>
      {showEditor && <EnvelopeDraftEditor envelope={envelope} />}
    </Stack>
  );
};
